// ═══════════════════════════════════════════════════════════
// Shared utilities for position grid components
// ═══════════════════════════════════════════════════════════

/** Currency symbol helper — shared across all position grids */
export const getCurrencySymbol = (currency?: string | null): string => {
  if (!currency) return '$ ';
  switch (currency.toUpperCase()) {
    case 'EUR': return '€ ';
    case 'GBP': return '£ ';
    case 'JPY':
    case 'CNY': return '¥ ';
    case 'ILS': return '₪ ';
    case 'USD':
    default: return '$ ';
  }
};

/** Format number as currency string (no symbol) */
export const formatCurrency = (value: number): string =>
  value.toLocaleString(undefined, { maximumFractionDigits: 0 });

/** Format quantity with up to 2 decimal places */
export const formatQuantity = (value: number): string =>
  value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Format an ISO date string or Date to "Mon YY" */
export const formatDate = (date: string | Date | any): string => {
  try {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  } catch {
    return '';
  }
};

/** Shared DataGrid sx styling for all position grids */
export const getGridSx = (extraSx?: Record<string, any>) => ({
  border: 'none',
  fontFamily: 'inherit',
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #e5e7eb',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'inherit',
    justifyContent: 'flex-start',
  },
  '& .MuiDataGrid-row.positions-grid-group': {
    backgroundColor: '#f3f4f6',
    '&:hover': { backgroundColor: '#e5e7eb' },
  },
  '& .MuiDataGrid-row.positions-grid-out-of-scope': {
    backgroundColor: '#fafafa',
    opacity: 0.6,
    '& .MuiDataGrid-cell': { color: '#9ca3af' },
    '&:hover': { backgroundColor: '#f5f5f5' },
  },
  '& .MuiDataGrid-row.positions-grid-ratio-po': {
    opacity: 0.5,
    '&:hover': { opacity: 0.8 },
  },
  '& .MuiDataGrid-columnHeaders': {
    borderBottom: '2px solid #d1d5db',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#374151',
    fontFamily: 'inherit',
  },
  '& .MuiDataGrid-columnHeader': {
    padding: '12px 16px',
    fontFamily: 'inherit',
    '&:focus': { outline: 'none' },
    '&:focus-within': { outline: 'none' },
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    overflow: 'visible',
    whiteSpace: 'normal',
    lineHeight: '1.2',
  },
  '& .MuiDataGrid-row': {
    '&:hover': { backgroundColor: '#f9fafb' },
  },
  ...extraSx,
});
